import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LexiTipsLogo from "@/assets/lexi-tips-logo.svg";
import "@/styles/_public-navbar.scss";
import "@/styles/_public-navbar.scss";
import {
  STRIPE_BASIC_PRODUCT_URL,
  getStripeUrlBasedOnEnvironment,
} from "@/utils/vars";
import { LuMenu } from "@/components/icons";
import MiniAuthOptions from "@/components/mini-auth-options";
import { Button } from "@/components/ui/button";
import { NewBadge, NewDot, isStillNew } from "@/components/ui/new-badge";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsSignedIn } from "@/hooks/useIsSignedIn";
import CustomMarquee from "./custom-marquee";

function Navbar() {
  const navigate = useNavigate();
  const isHomePage = window.location.pathname === "/";
  const isSignedIn = useIsSignedIn();

  useEffect(() => {
    // Check if we have a stored scroll target
    const scrollTarget = sessionStorage.getItem("scrollTarget");
    if (scrollTarget) {
      sessionStorage.removeItem("scrollTarget");
      const element = document.getElementById(scrollTarget);
      if (element) {
        requestAnimationFrame(() => {
          const navbarHeight = document.querySelector("nav").offsetHeight;
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: "smooth",
          });
        });
      }
    }
  }, []);

  const navLinks = [
    // { label: "Home", href: "/" },
    {
      label: "How it works",
      href: isHomePage ? "#howitworks" : "/#howitworks",
    },
    { label: "Meet Lexi", href: isHomePage ? "#meetlexi" : "/#meetlexi" },
    { label: "Features", href: isHomePage ? "#features" : "/#features" },
    { label: "Results", href: "/race-results", newUntil: "2026-12-01" },
    { label: "Pricing", href: isHomePage ? "#pricing" : "/#pricing" },
    { label: "Contact", href: isHomePage ? "#contact" : "/#contact" },
  ];

  const isActive = (link) => window.location.pathname === link.href;
  const hasNewLink = navLinks.some((link) => isStillNew(link.newUntil));

  const scrollToSection = (href) => {
    // If it's not an anchor link, use regular navigation
    if (!href.includes("#")) {
      navigate(href);
      return;
    }

    // If we're not on the home page, navigate to home first
    if (!isHomePage) {
      const hash = href.split("#")[1];
      sessionStorage.setItem("scrollTarget", hash);
      window.location.href = "/";
      return;
    }

    // Handle anchor scrolling when on home page
    const id = href.split("#")[1];
    const element = document.getElementById(id);
    if (element) {
      // Close mobile menu if it's open (for mobile devices)
      const sheetClose = document.querySelector("[data-radix-collection-item]");
      if (sheetClose) {
        sheetClose.click();
      }

      // Wait a bit longer on mobile to ensure menu is closed
      setTimeout(() => {
        requestAnimationFrame(() => {
          const navbarHeight = document.querySelector("nav").offsetHeight;
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementPosition - navbarHeight,
            behavior: "smooth",
          });
        });
      }, 100);
    }
  };

  // Separate components for mobile and desktop navigation
  const DesktopNavLinks = () => (
    <>
      {navLinks.map((link) => (
        <Button
          key={link.label}
          variant="ghost"
          className={`hover:text-brand-pink ${isActive(link) ? "text-brand-pink font-semibold" : ""}`}
          aria-current={isActive(link) ? "page" : undefined}
          onClick={() => scrollToSection(link.href)}
          data-gtm-element-id={`header_${link.label.toLowerCase()}_button-${navLinks.indexOf(link) + 1}`}
        >
          <span className="relative">
            {link.label}
            {isStillNew(link.newUntil) && <NewBadge variant="corner" />}
          </span>
        </Button>
      ))}
    </>
  );

  const MobileNavLinks = ({ className = "" }) => (
    <>
      {navLinks.map((link) => (
        <SheetClose asChild key={link.label}>
          <Button
            variant="ghost"
            className={`hover:text-brand-pink w-full justify-start ${className} ${isActive(link) ? "text-brand-pink font-semibold bg-[#FDF2F8]" : ""}`}
            aria-current={isActive(link) ? "page" : undefined}
            onClick={() => scrollToSection(link.href)}
            data-gtm-element-id={`header_mobile_${link.label.toLowerCase()}_button-${navLinks.indexOf(link) + 1}`}
          >
            {link.label}
            {isStillNew(link.newUntil) && <NewBadge className="ml-auto" />}
          </Button>
        </SheetClose>
      ))}
    </>
  );

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm">
        <CustomMarquee />
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <span className="text-2xl font-black">
                  {/* Add svg logo from public folder */}
                  <img
                    src={LexiTipsLogo}
                    alt="Lexi.tips Logo"
                    className="h-12 w-30"
                  />
                </span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <DesktopNavLinks />
            </div>

            <div className="hidden md:flex items-center space-x-4">
              {isSignedIn ? (
                // Signed in: straight to their tips, never back to /signup
                <Button
                  variant="outline"
                  className="inline-block px-8 py-3 bg-brand-pink text-white rounded-lg font-semibold hover:bg-[#e391c4] transition-all duration-200"
                  onClick={() => navigate("/todays-tips")}
                  data-gtm-element-id="header_mytips_button-1"
                >
                  My tips
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="hidden lg:inline-block px-8 py-3 bg-white text-brand-pink border border-brand-pink rounded-lg font-semibold hover:bg-[#FDF2F8] transition-all duration-200"
                    onClick={() => navigate("/todays-tips")}
                    data-gtm-element-id="header_signin_button-1"
                  >
                    Sign in
                  </Button>
                  <Button
                    variant="outline"
                    className="inline-block px-8 py-3 bg-brand-pink text-white rounded-lg font-semibold hover:bg-[#e391c4] transition-all duration-200"
                    onClick={() => navigate("/signup")}
                    data-gtm-element-id="header_signup_button-1"
                  >
                    Get tips now!
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    aria-label={
                      hasNewLink ? "Open menu, something new" : "Open menu"
                    }
                    data-gtm-element-id="header_menu_button-1"
                  >
                    <LuMenu className="h-6 w-6" />
                    {hasNewLink && <NewDot />}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right">
                  <SheetHeader>
                    <SheetTitle className="text-left">Menu</SheetTitle>
                  </SheetHeader>
                  <div className="mt-8 flex flex-col space-y-4">
                    <MobileNavLinks className="text-left" />
                    <MiniAuthOptions isSignedIn={isSignedIn} />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navbar;
